
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

export default function ContactSection() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validación mejorada
    if (!formData.name.trim()) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 3000);
      return;
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 3000);
      return;
    }
    if (!formData.message.trim()) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 3000);
      return;
    }
    if (formData.message.length > 500) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 3000);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || 'No proporcionado',
        company: formData.company || 'No proporcionado',
        message: formData.message
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d64n33sbd5onm6baplpg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody
      });

      if (response.ok) {
        setSubmitStatus('success');
        // Limpiar formulario
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: '',
        });
        // Ocultar mensaje de éxito después de 5 segundos
        setTimeout(() => setSubmitStatus('idle'), 5000);
      } else {
        setSubmitStatus('error');
        setTimeout(() => setSubmitStatus('idle'), 3000);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Limitar el mensaje a 500 caracteres
    if (name === 'message' && value.length > 500) {
      return;
    }
    
    setFormData({
      ...formData,
      [name]: value
    });
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Business Hours */}
          <div className="flex flex-col items-center justify-center bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-12 shadow-lg">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-[#F97316] rounded-full mb-6">
                <i className="ri-time-line text-4xl text-white"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                {t('contact.info.hours.title')}
              </h3>
              <div className="space-y-3">
                <p className="text-xl font-semibold text-gray-800">
                  {t('contact.info.hours.everyday')}
                </p>
                <p className="text-5xl font-bold text-[#F97316]">24/7</p>
                <div className="flex items-center justify-center gap-2 mt-4">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <p className="text-sm text-gray-600 font-medium">
                    {t('contact.info.hours.available')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100">
            <div className="mb-8">
              <h3 className="text-3xl font-bold text-gray-900 mb-2">
                {t('contact.form.title')}
              </h3>
              <p className="text-sm text-gray-500">{t('contact.description')}</p>
            </div>

            <form id="contact-form" onSubmit={handleSubmit} className="space-y-6" data-readdy-form>
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('contact.form.name')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t('contact.form.name.placeholder')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F97316] focus:border-transparent transition-all text-sm"
                  required
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('contact.form.email')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t('contact.form.email.placeholder')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F97316] focus:border-transparent transition-all text-sm"
                  required
                />
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('contact.form.phone')}
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder={t('contact.form.phone.placeholder')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F97316] focus:border-transparent transition-all text-sm"
                />
              </div>

              {/* Company */}
              <div>
                <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('contact.form.company')}
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder={t('contact.form.company.placeholder')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F97316] focus:border-transparent transition-all text-sm"
                />
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('contact.form.message')} <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                  placeholder={t('contact.form.message.placeholder')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F97316] focus:border-transparent transition-all resize-none text-sm"
                  required
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.message.length}/500 {t('contact.form.message.hint')}
                </p>
              </div>

              {/* Success/Error Messages */}
              {submitStatus === 'success' && (
                <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4 flex items-start gap-3 animate-pulse">
                  <i className="ri-checkbox-circle-fill text-green-500 text-2xl"></i>
                  <div>
                    <p className="font-bold text-green-800 text-base">{t('contact.form.success.title')}</p>
                    <p className="text-sm text-green-700 mt-1">{t('contact.form.success.desc')}</p>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4 flex items-start gap-3">
                  <i className="ri-error-warning-fill text-red-500 text-2xl"></i>
                  <div>
                    <p className="font-semibold text-red-800">{t('contact.form.error.general')}</p>
                    <p className="text-sm text-red-600 mt-1">
                      {!formData.name.trim() && t('contact.form.error.name')}
                      {formData.name.trim() && (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) && t('contact.form.error.email')}
                      {formData.name.trim() && formData.email.trim() && !formData.message.trim() && t('contact.form.error.message')}
                    </p>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer text-base"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    {t('contact.form.sending')}
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-send-plane-fill"></i>
                    {t('contact.form.submit')}
                  </span>
                )}
              </button>

              {/* Privacy Notice */}
              <p className="text-xs text-gray-500 text-center">
                <i className="ri-lock-line mr-1"></i>
                {t('contact.form.privacy')}
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
